import { Location } from 'history';
import { isAuthenticated } from 'mk/bazaar/common/userUtils';
import { analyticsStoreEvent, toastAdd } from 'mk2/actions';
import CommentList from 'mk2/apps/blogs/components/CommentList';
import {
    commentCreate,
    commentCreateUnauthenticated,
    commentDelete,
    commentReply,
    commentsLoad,
    commentToggleHighlight,
} from 'mk2/apps/blogs/containers/Comments/Comments.actions';
import { CommentsState } from 'mk2/apps/blogs/containers/Comments/Comments.reducers';
import { getCommentsNonDeleted, getCommentsState } from 'mk2/apps/blogs/containers/Comments/Comments.selectors';
import { ArticleEntity, BlogPostEntity, CommentEntity, CommentSchema } from 'mk2/apps/blogs/schemas';
import { getModelAndId } from 'mk2/apps/blogs/utils';
import { postSendHeart } from 'mk2/apps/forum/actions';
import { CommentFormData } from 'mk2/apps/forum/components/CommentCreateEditForm';
import LoadError from 'mk2/components/errors/LoadError';
import ServerErrorToast from 'mk2/components/toasts/ServerErrorToast';
import { complaintInitToggle } from 'mk2/containers/Complaint/Complaint.actions';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import { getComplaintsShownForBlogComments } from 'mk2/containers/Complaints/Complaints.selectors';
import { likeToggle } from 'mk2/containers/Like/Like.actions';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import {
    AppState,
    getRequestDeviceIsIOS,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    getResponseRenderedAt,
} from 'mk2/reducers';
import { isFailure, LoadingState } from 'mk2/reducers/loadingState';
import { UserEntity } from 'mk2/schemas';
import { getDenormalizedEntities } from 'mk2/selectors';
import { subscribeJam, unsubscribeJam } from 'mk2/services/jam';
import React from 'react';
import { connect } from 'react-redux';
import styles from './Comments.mscss';

interface OwnProps {
    postKey: string;
    count: number;
    inGroups?: boolean;
    inPrivateGroup?: boolean;
    isPreloaded?: boolean;
    hasBlueToolbar?: boolean;
    moderators?: UserEntity[];
    location: Location;
    showFakeTextareaIfCantAdd?: boolean;
    post?: ArticleEntity | BlogPostEntity;

    onBlur?();
    onCommentDelete?(commentId: number, key: string);
    onFocus?();
    onToggleMenuMore?(commentId: number);
    onSetTextareaRef?(ref: any);
}

interface StateProps {
    comments: CommentEntity[];
    commentToBeHighlighted: number | 'last';
    loadingState: LoadingState;
    errorMessage: string;
    canAdd: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    renderedAt: number;
    isIOS: boolean;
    mobile: boolean;
    initialValues: CommentFormData;
    complaintsOpened: boolean[];
}

interface DispatchProps {
    onLoad(key: string);
    onCreate(commentFd: CommentFormData, key: string);
    onLike(entity: string, id: number);
    onDelete(commentId: number, key: string);
    onReport(entityType: ComplaintTargetType, commentId: number);
    onReply(key: string, username: string);
    onToggleHighlight(key: string, commentId: number);

    onAddCommentUnauthenticated();
    onFailureToast();
    onSendHeart();
    onStoreEvent(name: string, props: any);
}

type Props = OwnProps & StateProps & DispatchProps;

class Comments extends React.Component<Props> {

    public shouldComponentUpdate(n: Props) {
        const p = this.props;

        if (p.comments.length === n.comments.length) {
            for (let i = 0; i < p.comments.length; i++) {
                if (p.comments[i].likeable.likesCount !== n.comments[i].likeable.likesCount) {
                    return true;
                }
                if (p.comments[i].deletedAt !== n.comments[i].deletedAt) {
                    return true;
                }
            }
        } else {
            return true;
        }

        return p.complaintsOpened.join() !== n.complaintsOpened.join()
            || p.commentToBeHighlighted !== n.commentToBeHighlighted
            || p.loadingState !== n.loadingState
            || p.moderators !== n.moderators
            || p.canAdd !== n.canAdd;
    }

    // public componentWillMount() {
    //     const { loadingState, renderedAt } = this.props;
    //
    //     // Load only if not loaded on server and is not older than 30s
    //     if (loadingState !== LoadingState.SUCCESS_ON_SERVER || renderedAt < Date.now() - 30 * 1000) {
    //         this.load();
    //     }
    // }

    public componentDidMount() {
        const { postKey } = this.props;

        // do not need to call onLoad(), initial data are passed from django via html
        const modelAndId = getModelAndId(postKey);
        subscribeJam('comment:' + modelAndId[0] + ':' + modelAndId[1]);
    }

    public componentWillUnmount() {
        const { postKey } = this.props;

        // do not need to call onLoad(), initial data are passed from django via html
        const modelAndId = getModelAndId(postKey);
        unsubscribeJam('comment:' + modelAndId[0] + ':' + modelAndId[1]);
    }

    public render() {
        const {
            loadingState, comments, requestUser, requestUserPermissions, mobile, post, postKey, location,
            inGroups, initialValues, inPrivateGroup, isIOS, count, complaintsOpened, canAdd,
            commentToBeHighlighted, location: { hash }, moderators, showFakeTextareaIfCantAdd, hasBlueToolbar,
            onReport, onSendHeart, onToggleMenuMore, onBlur, onFocus, onAddCommentUnauthenticated, onSetTextareaRef,
            onStoreEvent,
        } = this.props;

        const id = parseFloat(getModelAndId(postKey)[1]);
        const hashValue = hash && hash.indexOf('-') !== -1 ? hash.split('-')[1] : null;
        const toBeHighlightedFromHash = hashValue === 'last' && comments && comments.length > 0 ? comments[comments.length - 1].id : parseFloat(hashValue);
        const toBeHighlightedFromState = commentToBeHighlighted && (commentToBeHighlighted === 'last' ?
                (comments && comments.length > 0 && comments[comments.length - 1].id) :
                commentToBeHighlighted
        );
        let highlightedId = toBeHighlightedFromState ? toBeHighlightedFromState : toBeHighlightedFromHash;
        if (isNaN(highlightedId)) {
            highlightedId = null;
        }

        return (
            <div className={styles.Comments}>
                {loadingState !== LoadingState.FAILURE && loadingState !== LoadingState.FAILURE_ON_SERVER ?
                    <CommentList
                        comments={comments}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        canAdd={canAdd}
                        hasBlueToolbar={hasBlueToolbar}
                        showFakeTextareaIfCantAdd={showFakeTextareaIfCantAdd}
                        post={post}
                        postId={id}
                        count={count}
                        highlightTriggeredId={highlightedId}
                        highlightStartedId={toBeHighlightedFromState}
                        isIOS={isIOS}
                        mobile={mobile}
                        moderators={moderators}
                        postKey={postKey}
                        loadingState={loadingState}
                        inGroups={inGroups}
                        initialValues={initialValues}
                        inPrivateGroup={inPrivateGroup}
                        complaintsOpened={complaintsOpened}
                        location={location}
                        onBlur={onBlur}
                        onCreate={this.onCreate}
                        onDidHighlight={this.handleOnDidHighlight}
                        onFocus={onFocus}
                        onLike={this.onLike}
                        onReply={this.onReply}
                        onDelete={this.onDelete}
                        onReport={onReport}
                        onSendHeart={onSendHeart}
                        onToggleMenuMore={onToggleMenuMore}
                        onAddCommentUnauthenticated={onAddCommentUnauthenticated}
                        onSetTextareaRef={onSetTextareaRef}
                        onStoreEvent={onStoreEvent}
                    /> : null
                }

                {isFailure(loadingState) ? (
                    <LoadError onRetry={this.load} />
                ) : null}
            </div>
        );
    }

    public componentWillMount() {
        const { comments, count, isPreloaded } = this.props;
        if (!isPreloaded && (count === 0 || comments.length !== count)) {
            this.load();
        }
    }

    private load = () => {
        const { onLoad, postKey } = this.props;
        onLoad(postKey);
    };

    private handleOnDidHighlight = (commentId: number) => {
        const { onToggleHighlight, postKey } = this.props;
        onToggleHighlight(postKey, commentId);
    };

    private onCreate = (commentFd: CommentFormData) => {
        const { onCreate, postKey } = this.props;
        onCreate(commentFd, postKey);
    };

    private onLike = (commentId: number) => {
        this.props.onLike('comment', commentId);
    };

    private onReply = (username: string) => {
        const { postKey, onReply } = this.props;
        onReply(postKey, username);
    };

    private onDelete = (commentId: number) => {
        const { postKey, onCommentDelete, onDelete } = this.props;
        if (onCommentDelete) {
            onCommentDelete(commentId, postKey);
        } else {
            onDelete(commentId, postKey);
        }
    };
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    const { postKey } = ownProps;

    const commentsState: CommentsState = getCommentsState(state, postKey);
    const commentsAll = getDenormalizedEntities<CommentEntity>(state, CommentSchema, commentsState.ids);
    const comments = ownProps.inGroups ? commentsAll : getCommentsNonDeleted(commentsAll);
    const requestUser = getRequestUser(state);

    return {
        comments,
        commentToBeHighlighted: commentsState.commentToBeHighlighted,
        loadingState: commentsState.loadingState,
        errorMessage: commentsState.errorMessage,
        // Never allow canAdd for non-authenticated users
        canAdd: isAuthenticated(requestUser) && commentsState.canAdd,
        requestUser,
        requestUserPermissions: getRequestPermissions(state),
        renderedAt: getResponseRenderedAt(state),
        isIOS: getRequestDeviceIsIOS(state),
        mobile: getRequestDeviceMobile(state),
        initialValues: {
            text: '',
            photos: [],
            sticker: null,
        },
        complaintsOpened: getComplaintsShownForBlogComments(state, comments),
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onLoad: commentsLoad,
    onLike: likeToggle,
    onCreate: commentCreate,
    onDelete: commentDelete,
    onReply: commentReply,
    onReport: (entity: string, id: number) => complaintInitToggle(entity, id, true),
    onToggleHighlight: commentToggleHighlight,

    onAddCommentUnauthenticated: commentCreateUnauthenticated,
    onFailureToast: () => toastAdd('error', {
        component: ServerErrorToast,
    }),
    onSendHeart: postSendHeart,
    onStoreEvent: analyticsStoreEvent,
};

export default connect(mapStateToProps, mapDispatchToProps)(Comments);
